class ProductCard extends HTMLElement {
  constructor() {
    super();
    this.SRCSET_WIDTHS = [165, 360, 533, 720, 940, 1066];
    this.hasColorSwatches = this.dataset.cardColorSwatches === "true";
    this.initColorSwatches = this.initColorSwatches.bind(this);
  }
  connectedCallback() {
    this.hasColorSwatches = this.dataset.cardColorSwatches === "true";
    if (this.hasColorSwatches) {
      this.initColorSwatches();
    }
  }
  initColorSwatches() {
    if (this.getAttribute("data-swatch-initialized") === "true") return;
    const allInputs = this.querySelectorAll(".js-color-swatches input");
    allInputs.forEach((input) => {
      input.addEventListener("click", (event) => {
        if (event.currentTarget.checked) {
          const fallbackVariantId = event.currentTarget.dataset?.variantId;
          this.handleSwatchChange(fallbackVariantId);
          this.updateCardInputAttr(allInputs, event.currentTarget);
        }
      });
    });
    this.setAttribute("data-swatch-initialized", "true");
  }
  updateCardInputAttr(allInputs, currentInput) {
    if (!currentInput || !allInputs || allInputs.length === 0) return;
    allInputs.forEach((input) => {
      input.removeAttribute("checked");
    });
    currentInput.setAttribute("checked", true);
  }
  handleSwatchChange(fallbackVariantId) {
    const productVariantsJson = this.querySelector(
      "[data-product-card-variants]"
    );
    if (!productVariantsJson) return;
    const productVariants = JSON.parse(productVariantsJson.textContent);
    const selectedInput = this.querySelector(
      ".js-color-swatches input:checked"
    );
    if (!selectedInput) return;
    const selectedValue = selectedInput.value;
    const matchedVariant = productVariants.find((variant) => {
      return variant.options.includes(selectedValue);
    });
    const variantId = matchedVariant ? matchedVariant.id : fallbackVariantId;
    this.updateLinkOverlay(variantId);
    this.enableAddToCart();
    this.updateAddToCartButton(variantId);
    const productFeaturedMediaJson = this.querySelector(
      "[data-product-card-feat-media]"
    );
    const productFeaturedMedia = JSON.parse(
      productFeaturedMediaJson.textContent
    );
    const featuredMedia =
      matchedVariant.featured_media?.preview_image ||
      matchedVariant.featured_image ||
      productFeaturedMedia?.preview_image ||
      productFeaturedMedia;
    this.updateImageElement(featuredMedia);
  }
  generateSrcset(src, maxW) {
    const url = new URL(src, window.location.origin);
    return this.SRCSET_WIDTHS.filter((w) => w <= maxW)
      .map((w) => {
        url.searchParams.set("width", w);
        return `${url.href} ${w}w`;
      })
      .join(", ");
  }
  generateSizes() {
    const ratio = parseFloat(this.dataset.productRatio) || 1;
    const fit = this.dataset.productFit || "cover";
    const cols = Number(this.dataset.productColumns) || 1;
    const imgAspect = parseFloat(this.dataset.originalAspectRatio) || ratio;
    let calcRatio = 1;
    if (fit === "cover" && imgAspect > ratio) {
      calcRatio = +(imgAspect / ratio).toFixed(2);
    }
    let sizes = `(min-width: 1200px) calc(${calcRatio} * (100vw / ${cols}))`;
    if (cols >= 3) {
      sizes += `, (min-width: 990px) calc(${calcRatio} * (100vw / 3))`;
    }
    if (cols >= 2) {
      sizes += `, (min-width: 576px) calc(${calcRatio} * (100vw / 2))`;
    }
    const mobileSize = `calc(${calcRatio} * 100vw)`;
    sizes += `, ${mobileSize}`;
    return sizes;
  }
  normalizeImageUrl(src) {
    const url = new URL(src, window.location.origin);
    const version = url.searchParams.get("v");
    return url.host + url.pathname + (version ? `?v=${version}` : "");
  }
  updateImageElement(featuredMedia) {
    const primaryImage = this.querySelector(".media--first");
    if (!featuredMedia || !primaryImage) return;
    const secondaryImage = this.querySelector(".media--second");
    const newSrc = featuredMedia.src;
    const newSrcset = this.generateSrcset(
      featuredMedia.src,
      featuredMedia.width
    );
    const sizes = this.generateSizes();
    const altText = featuredMedia.alt || this.dataset.productTitle;
    if (
      this.normalizeImageUrl(primaryImage.src) ===
      this.normalizeImageUrl(newSrc)
    ) {
      if (secondaryImage) {
        const priPath = new URL(primaryImage.src).pathname;
        const secPath = new URL(secondaryImage.src).pathname;
        if (priPath === secPath) {
          primaryImage.remove();
          secondaryImage.classList.replace("media--second", "media--first");
          return;
        }
      }
      return;
    }
    primaryImage.style.transition = "opacity 250ms ease-in-out";
    primaryImage.style.opacity = "0";
    if (secondaryImage) {
      secondaryImage.style.transition = "opacity 250ms ease-in-out";
      secondaryImage.style.opacity = "0";
    }
    const onFadeOut = (e) => {
      if (e.propertyName !== "opacity") return;
      primaryImage.removeEventListener("transitionend", onFadeOut);
      primaryImage.onload = () => {
        primaryImage.style.transition = "opacity 250ms ease-in-out";
        primaryImage.style.opacity = "1";
        primaryImage.onload = null;
        primaryImage.style.transition = "";
      };
      primaryImage.src = newSrc;
      primaryImage.srcset = newSrcset;
      primaryImage.sizes = sizes;
      primaryImage.alt = altText;
      if (secondaryImage) secondaryImage.remove();
    };
    primaryImage.addEventListener("transitionend", onFadeOut);
  }
  enableAddToCart() {
    const btn = this.querySelector('.card__add-to-cart button[name="add"]');
    if (!btn) return;
    const label = btn.querySelector("[data-add-to-cart-label]");
    const sold = btn.querySelector("[data-sold-out-label]");
    const errMsg = this.querySelector(".product-form__error-message-wrapper");
    btn.setAttribute("aria-disabled", "false");
    label?.classList.remove("hidden");
    sold?.classList.add("hidden");
    errMsg?.setAttribute("hidden", "");
  }
  updateAddToCartButton(variantId) {
    const btn = this.querySelector('.card__add-to-cart input[name="id"]');
    if (!btn) return;
    btn.value = variantId;
  }
  updateLinkOverlay(variantId) {
    if (!variantId) return;
    const linkOverlay = this.querySelector("[data-card-swatch-link]");
    if (!linkOverlay) return;
    const currentHref = linkOverlay.getAttribute("href");
    const newUrl = new URL(currentHref, window.location.origin);
    newUrl.searchParams.set("variant", variantId);
    linkOverlay.setAttribute("href", newUrl.toString());
  }
}
if (!customElements.get("product-card")) {
  customElements.define("product-card", ProductCard);
}
class ProductCardSmall extends ProductCard {
  constructor() {
    super();
  }
  generateSizes() {
    const ratio = parseFloat(this.dataset.productRatio) || 1;
    const fit = this.dataset.productFit || "cover";
    const cols = Number(this.dataset.productColumns) || 1;
    const imgAspect = parseFloat(this.dataset.originalAspectRatio) || ratio;
    let calcRatio = 1;
    if (fit === "cover" && imgAspect > ratio) {
      calcRatio = +(imgAspect / ratio).toFixed(2);
    }
    let sizes = `(min-width: 1200px) calc(${calcRatio} * (100vw / ${cols}))`;
    if (cols >= 3) {
      sizes += `, (min-width: 990px) calc(${calcRatio} * (100vw / 3))`;
    }
    if (cols >= 2) {
      sizes += `, (min-width: 576px) calc(${calcRatio} * (100vw / 2))`;
    }
    const mobileSize = `calc(${calcRatio} * 100vw)`;
    sizes += `, ${mobileSize}`;
    return sizes;
  }
}
if (!customElements.get("product-card-small")) {
  customElements.define("product-card-small", ProductCardSmall);
}
